import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardMedia,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNotify, useRefresh } from "react-admin";
import {
  uploadAndUpdateComingSoon,
  updateComingSoonStatus,
} from "../../../Provider/parseDataProvider";

export const ComingSoonDialog = ({ open, onClose, record, isEnabling }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset state when dialog opens/closes or record changes
  React.useEffect(() => {
    if (open && record) {
      setImageFile(null);
      setImagePreview(record?.comingSoonImage || null);
    } else if (!open) {
      // Clear state when dialog closes
      setImageFile(null);
      setImagePreview(null);
    }
  }, [open, record]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        notify("Please select a valid image file", { type: "error" });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        notify("Image size should be less than 5MB", { type: "error" });
        return;
      }

      // Clean up previous object URL if it exists
      if (imageFile) {
        URL.revokeObjectURL(imagePreview);
      }

      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Cleanup object URLs on unmount
  React.useEffect(() => {
    return () => {
      if (imageFile && imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imageFile, imagePreview]);

  const handleConfirm = async () => {
    if (isEnabling && !imageFile) {
      notify("Please upload an image for Coming Soon", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      if (isEnabling && imageFile) {
        // Upload image and enable Coming Soon (backend handles both)
        await uploadAndUpdateComingSoon(record.id, imageFile, true);
      } else {
        // Just update status without image
        await updateComingSoonStatus(record.id, isEnabling);
      }

      notify(
        isEnabling
          ? "Coming Soon enabled successfully!"
          : "Coming Soon disabled successfully!",
        { type: "success" }
      );
      refresh();
      onClose();
    } catch (error) {
      notify(error.message || "Failed to update Coming Soon status", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    setLoading(true);
    try {
      // Disable Coming Soon and clear image
      await updateComingSoonStatus(record.id, false);
      
      notify("Coming Soon disabled successfully!", { type: "success" });
      refresh();
      onClose();
    } catch (error) {
      notify(error.message || "Failed to disable Coming Soon", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditImage = async () => {
    if (!imageFile) {
      notify("Please select a new image", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      // Upload new image and keep Coming Soon enabled (backend handles both)
      await uploadAndUpdateComingSoon(record.id, imageFile, true);

      notify("Coming Soon image updated successfully!", { type: "success" });
      refresh();
      onClose();
    } catch (error) {
      notify(error.message || "Failed to update image", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{color:"#FFC107",}}>
        {isEnabling ? "Enable Coming Soon" : "Disable Coming Soon" }
      </DialogTitle>
      <DialogContent>
        {isEnabling ? (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 , color:"#FFC107"}}>
              Upload an image for "{record?.name}" Coming Soon announcement
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#FFA000",
                  },
                }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Box>

            {imagePreview && (
              <Card sx={{ maxWidth: 400, mt: 2 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={imagePreview}
                  alt="Coming Soon Preview"
                />
              </Card>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              What would you like to do with "{record?.name}"?
            </Typography>

            {/* Show current image if exists and no new image selected */}
            {imagePreview && !imageFile && (
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <Typography variant="caption" sx={{ p: 1, display: "block" }}>
                  Current Image:
                </Typography>
                <CardMedia
                  component="img"
                  height="150"
                  image={imagePreview}
                  alt="Current Coming Soon Image"
                />
              </Card>
            )}

            {/* Upload new image option */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#FFA000",
                  },
                }}
              >
                Choose New Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </Button>
            </Box>

            {/* Preview new image if selected */}
            {imageFile && (
              <Card sx={{ maxWidth: 400, mb: 2 }}>
                <Typography variant="caption" sx={{ p: 1, display: "block" }}>
                  New Image Preview:
                </Typography>
                <CardMedia
                  component="img"
                  height="150"
                  image={URL.createObjectURL(imageFile)}
                  alt="New Image Preview"
                />
              </Card>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {imageFile && (
                <Button
                  variant="contained"
                  onClick={handleEditImage}
                  disabled={loading}
                  sx={{
                    background: "#FFC107",
                    color: "#0A0A0A",
                    "&:hover": {
                      backgroundColor: "#FFA000",
                    },
                  }}
                >
                  {loading ? "Updating..." : "Update Image"}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisable}
                disabled={loading}
              >
                Turn Off Coming Soon
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {isEnabling && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={loading || !imagePreview}
          >
            {loading ? "Saving..." : "Confirm"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
